import { Component, Input, ViewEncapsulation, CUSTOM_ELEMENTS_SCHEMA, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { SwiperOptions } from 'swiper/types';
import { SvgIconComponent } from 'angular-svg-icon';
import { CommonModule } from '@angular/common';

/**
 * Swiper carousel wrapper component.
 *
 * @remarks Provides a configurable carousel/slider component using Swiper.js.
 * Supports custom options merging with sensible defaults for navigation,
 * pagination, and autoplay features. Handles responsive carousel behavior
 * with proper initialization after view creation.
 */
@Component({
  selector: 'app-swiper',
  templateUrl: './swiper.component.html',
  styleUrls: ['./swiper.component.scss'],
  encapsulation: ViewEncapsulation.None,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  standalone: true,
  imports: [CommonModule, SvgIconComponent],
})
export class SwiperComponent implements AfterViewInit {
  /** @remarks Custom Swiper configuration options provided by parent component */
  @Input() options: SwiperOptions = {};

  /** @remarks Reference to the swiper DOM element for configuration and initialization */
  @ViewChild('swiper') swiper!: ElementRef<any>;

  /** @remarks Default Swiper configuration with navigation, pagination, and basic settings */
  private defaultOptions: SwiperOptions = {
    slidesPerView: 1,
    spaceBetween: 0,
    navigation: {
      nextEl: '.next-button',
      prevEl: '.prev-button',
      enabled: true,
    },
    pagination: {
      clickable: true,
      el: '.pagination-wrapper',
    },
    autoplay: false,
  };

  /**
   * Lifecycle hook called after the component's view has been initialized.
   *
   * @remarks Merges custom options with defaults and initializes the Swiper instance.
   * Handles special cases for pagination and navigation configuration to ensure
   * proper element selectors and functionality. The configuration is applied
   * directly to the native element before initialization.
   */
  ngAfterViewInit(): void {
    const mergedOptions = { ...this.defaultOptions, ...this.options };

    // Ensure pagination el is set correctly if custom options are provided
    if (this.options.pagination && typeof this.options.pagination === 'object') {
      mergedOptions.pagination = {
        clickable: true,
        el: '.pagination-wrapper',
        ...(this.options.pagination as Record<string, any>),
      };
    }

    // Ensure navigation is configured correctly if custom options are provided
    if (this.options.navigation && typeof this.options.navigation === 'object') {
      mergedOptions.navigation = {
        nextEl: '.next-button',
        prevEl: '.prev-button',
        enabled: true,
        ...(this.options.navigation as Record<string, any>),
      };
    }

    Object.assign(this.swiper.nativeElement, mergedOptions);
    this.swiper.nativeElement.initialize();
  }
}
